/**
 * Definición de acciones y buckets del kanban LCL.
 *
 * Es el gemelo de `components/operations/columns/columnActions.js` del
 * módulo aéreo, pero vive dentro de `pages/lcl-requests/` y es
 * propiedad exclusiva del LCL: se puede cambiar sin ningún riesgo
 * para el aéreo.
 *
 * Convención:
 *  - `key`:     identificador único que recibe el `onAction` del padre
 *  - `label`:   texto a mostrar en botones/tooltips
 *  - `icon`:    nombre del icono (string) que se mapea en `ICON_MAP`
 *  - `variant`: 'primary' | 'menu' — primary = botón visible; menu = solo en ⋮
 */
import {
  Eye,
  Edit,
  Copy,
  CheckCircle2,
  Trash2,
  Hash,
} from 'lucide-react';

export const ICON_MAP = {
  Eye,
  Edit,
  Copy,
  CheckCircle2,
  Trash2,
  Hash,
};

/**
 * Lista de acciones por columna. El orden importa: la primera acción
 * cuyo `variant === 'primary'` se muestra como botón visible en la
 * tarjeta; el resto van al menú desplegable.
 *
 * A diferencia del aéreo, aquí NO hay acciones de cotizaciones de
 * proveedor (invitar / cargar cotización): ese subsistema todavía no
 * existe en LCL.
 */
export const COLUMN_ACTIONS = {
  cotizaciones: [
    { key: 'view', label: 'Ver detalle', icon: 'Eye', variant: 'menu' },
    { key: 'traceability', label: 'Trazabilidad', icon: 'Hash', variant: 'menu' },
    { key: 'close', label: 'Enviar (aprobar)', icon: 'CheckCircle2', variant: 'primary' },
    { key: 'delete', label: 'Eliminar', icon: 'Trash2', variant: 'menu' },
  ],
  'en-curso': [
    { key: 'view', label: 'Ver detalle', icon: 'Eye', variant: 'menu' },
    { key: 'traceability', label: 'Trazabilidad', icon: 'Hash', variant: 'menu' },
    { key: 'close', label: 'Aprobar', icon: 'CheckCircle2', variant: 'primary' },
    // Sin "Eliminar": una operación en curso no se borra, se cancela.
  ],
  finalizadas: [
    { key: 'view', label: 'Ver detalle', icon: 'Eye', variant: 'primary' },
    { key: 'traceability', label: 'Trazabilidad', icon: 'Hash', variant: 'menu' },
    { key: 'delete', label: 'Eliminar', icon: 'Trash2', variant: 'menu' },
  ],
  rechazadas: [
    { key: 'view', label: 'Ver detalle', icon: 'Eye', variant: 'primary' },
    { key: 'traceability', label: 'Trazabilidad', icon: 'Hash', variant: 'menu' },
    { key: 'delete', label: 'Eliminar', icon: 'Trash2', variant: 'menu' },
  ],
};

/** Devuelve la acción primaria (visible) de una columna. */
export const getPrimaryAction = (columnId) =>
  (COLUMN_ACTIONS[columnId] || []).find((a) => a.variant === 'primary');

/** Determina si una acción debe estar habilitada para una fila. */
export const isActionEnabled = (action, row) => {
  if (action.key === 'close') {
    return row?.estado !== 'Aprobada' && row?.estado !== 'Rechazada';
  }
  return true;
};

/** Definición de buckets: cada columna y los estados que agrupa. */
export const COLUMN_BUCKETS = [
  {
    id: 'cotizaciones',
    title: 'Cotizaciones',
    estados: ['Borrador'],
    accent: 'border-t-amber-400',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  {
    id: 'en-curso',
    title: 'Operaciones en curso',
    estados: ['Enviada'],
    accent: 'border-t-blue-500',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  {
    id: 'finalizadas',
    title: 'Finalizadas',
    estados: ['Aprobada'],
    accent: 'border-t-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  {
    id: 'rechazadas',
    title: 'Rechazadas',
    estados: ['Rechazada'],
    accent: 'border-t-rose-500',
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
  },
];
